import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { Location } from "react-router-dom";

import { createDemoApi } from "./api/demo";
import type { PortalApi } from "./api/portal";
import { AuthProvider, useSession } from "./auth/AuthProvider";
import type { AuthApi } from "./auth/authApi";
import { RequireAuth } from "./auth/RequireAuth";
import { Dashboard } from "./Dashboard";
import { MarketingLayout } from "./marketing/MarketingLayout";
import { Account } from "./pages/auth/Account";
import { CheckEmail } from "./pages/auth/CheckEmail";
import { ForgotPassword } from "./pages/auth/ForgotPassword";
import { Login } from "./pages/auth/Login";
import { Register } from "./pages/auth/Register";
import { ResetPassword } from "./pages/auth/ResetPassword";
import { VerifyEmail } from "./pages/auth/VerifyEmail";
import { Download } from "./pages/Download";
import { Features } from "./pages/Features";
import { Help } from "./pages/Help";
import { HelpGuide } from "./pages/HelpGuide";
import { Home } from "./pages/Home";
import { NotFound } from "./pages/NotFound";
import { Notifications } from "./pages/Notifications";
import { Privacy } from "./pages/Privacy";
import { Providers } from "./pages/Providers";
import { ReleaseDetail } from "./pages/ReleaseDetail";
import { Releases } from "./pages/Releases";
import { Spec } from "./pages/Spec";
import { Terms } from "./pages/Terms";

interface Props {
  api: PortalApi;
  authApi: AuthApi;
}

/**
 * The synthetic overview, built once at module scope for the same reason the
 * real one is: `useOverview` keys its effect on the api object.
 */
const demoApi = createDemoApi();

export function App({ api, authApi }: Props) {
  return (
    // Outside the routes, so the session is fetched once for the whole app
    // rather than again on every navigation.
    <AuthProvider api={authApi}>
      <ScrollToHash />
      <Routes>
        <Route element={<MarketingLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/features" element={<Features />} />
          <Route path="/releases" element={<Releases />} />
          <Route path="/releases/:version" element={<ReleaseDetail />} />
          <Route path="/download" element={<Download />} />
          <Route path="/providers" element={<Providers />} />
          <Route path="/help" element={<Help />} />
          <Route path="/help/:slug" element={<HelpGuide />} />
          <Route path="/spec" element={<Spec />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />

          {/* The account pages share the marketing shell: they are reached
              from it, and someone who lands on one from an email should still
              have a way back into the site. */}
          {/* Gated, and inside the marketing shell rather than the dashboard's:
              it is a settings page, not part of the fleet view. */}
          <Route
            path="/account"
            element={
              <RequireAuth>
                <Account />
              </RequireAuth>
            }
          />

          {/* Same placement and reasoning as `/account` above: a settings
              page reached from the profile menu, not part of the fleet view
              under `/dashboard`. */}
          <Route
            path="/notifications"
            element={
              <RequireAuth>
                <Notifications api={api} />
              </RequireAuth>
            }
          />

          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/check-email" element={<CheckEmail />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* The fleet dashboard, behind the login. It shares this shell —
              only the content inside `<Outlet/>` is specific to it. The
              worker refuses unauthenticated requests regardless; the gate
              here is so the UI doesn't render a page it cannot fill. */}
          <Route
            path="/dashboard/*"
            element={
              <RequireAuth>
                <Dashboard api={api} />
              </RequireAuth>
            }
          />

          {/* The same dashboard on synthetic data, public and ungated. It used
              to be what every signed-in user saw, which meant the product's
              own customers were shown fiction about their own backups. As a
              page anyone can look at before buying, it is doing an honest
              job. */}
          <Route path="/demo/*" element={<DemoRoute />} />

          <Route path="*" element={<NotFound />} />
        </Route>

        {/* `/app` was the dashboard's address before it moved to `/dashboard`.
            Old bookmarks and links still point there, so it redirects instead
            of landing on the marketing shell's 404. */}
        <Route path="/app/*" element={<LegacyDashboardRedirect />} />
      </Routes>
    </AuthProvider>
  );
}

/**
 * Makes cross-page fragment links behave the way the browser's own do.
 *
 * A `<Link to="/features#security">` navigates with `history.pushState`, and
 * `pushState` never scrolls — so every one of them (the villain cards, the
 * feature tiles, the "Defeats:" credits) landed on the right page at whatever
 * offset the previous one had been left at, which for a link clicked halfway
 * down the home page is the middle of the feature list. Restores both halves
 * of the behaviour a fragment link is expected to have: scroll to the target
 * when there is one, and to the top of the page when there isn't.
 */
function ScrollToHash() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const target = document.getElementById(decodeURIComponent(hash.slice(1)));
      // Guarded rather than called outright: `scrollIntoView` is one of the
      // layout APIs jsdom does not implement, and these routes render there.
      if (target && typeof target.scrollIntoView === "function") {
        target.scrollIntoView({ block: "start" });
        return;
      }
      // A hash naming nothing on this page falls through to the top, rather
      // than leaving the reader wherever the last page happened to be.
    }
    // Not `window.scrollTo`, which jsdom implements as a noisy "not
    // implemented" error; assigning scrollTop is a real scroll in a browser
    // and a silent no-op there.
    document.documentElement.scrollTop = 0;
  }, [pathname, hash]);

  return null;
}

/** Rewrites a path prefix, preserving the query string and hash after it. */
function rewritePrefix(location: Location, from: RegExp, to: string) {
  return location.pathname.replace(from, to) + location.search + location.hash;
}

/** Rewrites `/app/...` to `/dashboard/...`, preserving whatever came after. */
function LegacyDashboardRedirect() {
  const location = useLocation();
  return <Navigate replace to={rewritePrefix(location, /^\/app/i, "/dashboard")} />;
}

/**
 * The demo is a preview for someone deciding whether to sign up — not a
 * second dashboard for someone who already has. A signed-in visitor lands
 * here anyway (a marketing link, an old bookmark), and this shell's header
 * is session-aware regardless of which dashboard it's wrapping: it would
 * show their real profile menu — real email, a working sign-out — over a
 * page telling them to "create an account". Send them to their own instead.
 */
function DemoRoute() {
  const { user, checking } = useSession();
  const location = useLocation();

  if (!checking && user) {
    return <Navigate replace to={rewritePrefix(location, /^\/demo/, "/dashboard")} />;
  }

  return <Dashboard api={demoApi} />;
}
